/**
 * PieChart — pure-SVG donut chart with animated slices and a legend.
 *
 * Each slice renders as a stroked <circle> on a single ring; we use
 * stroke-dasharray to size each slice and stroke-dashoffset to position it.
 * On mount the dasharray animates from 0 to the slice's length, staggered
 * by index for a "draw in" effect.
 *
 * Render is pure presentation — pass in a `data` array of slices and the
 * component handles geometry, animation, and the legend.
 */

import React, { useMemo } from 'react';

export interface PieSlice {
  key: string;
  label: string;
  value: number;
}

interface PieChartProps {
  data: PieSlice[];
  palette: string[];
  centerLabel?: string;
  centerSubLabel?: string;
}

const RADIUS = 40;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export const PieChart: React.FC<PieChartProps> = ({ data, palette, centerLabel, centerSubLabel }) => {
  const total = useMemo(
    () => data.reduce((sum, d) => sum + Math.max(0, d.value), 0),
    [data],
  );

  // Skip zero-value slices for the visual ring but keep them in the legend so
  // the user sees that the dimension exists with no activity yet.
  const visibleSlices = useMemo(() => data.filter(d => d.value > 0), [data]);

  // Pre-compute each slice's arc length and rotation offset around the ring.
  const geometry = useMemo(() => {
    let cumulative = 0;
    return visibleSlices.map((s, i) => {
      const fraction = total > 0 ? s.value / total : 0;
      const length = fraction * CIRCUMFERENCE;
      const offset = -cumulative; // negative because stroke-dashoffset moves "backwards"
      cumulative += length;
      return {
        ...s,
        index: i,
        length,
        offset,
        color: palette[data.findIndex(d => d.key === s.key) % palette.length],
      };
    });
  }, [visibleSlices, total, palette, data]);

  return (
    <div className="analytics-pie">
      {total === 0 ? (
        <div className="text-muted small py-5">No data yet.</div>
      ) : (
        <svg className="analytics-pie__svg" viewBox="-56 -56 112 112" role="img">
          {/* Background ring so empty space reads as "ring not absent" */}
          <circle cx={0} cy={0} r={RADIUS} fill="none" stroke="#f3f4f6" strokeWidth={22} />
          {geometry.map(g => (
            <circle
              key={g.key}
              className="analytics-pie__slice"
              cx={0}
              cy={0}
              r={RADIUS}
              stroke={g.color}
              strokeDashoffset={g.offset}
              style={{
                ['--slice-len' as string]: g.length.toFixed(3),
                ['--slice-index' as string]: g.index,
              }}
            />
          ))}
          {centerLabel && (
            <text className="analytics-pie__center" x={0} y={centerSubLabel ? -3 : 0}>
              {centerLabel}
              {centerSubLabel && (
                <tspan className="analytics-pie__center-sub" x={0} dy={10}>
                  {centerSubLabel}
                </tspan>
              )}
            </text>
          )}
        </svg>
      )}

      <div className="analytics-legend">
        {data.map((d, i) => {
          const pct = total > 0 ? (d.value / total) * 100 : 0;
          return (
            <div
              key={d.key}
              className="analytics-legend__row"
              style={{ ['--row-index' as string]: i }}
            >
              <span
                className="analytics-legend__swatch"
                style={{ background: palette[i % palette.length] }}
              />
              <span className="analytics-legend__label">{d.label}</span>
              <span className="analytics-legend__count">{d.value.toLocaleString()}</span>
              <span className="analytics-legend__pct">{pct.toFixed(1)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PieChart;
